import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('tire_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('tire_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (tire, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.size === tire.size);
      
      if (existingItem) {
        return prevCart.map(item =>
          item.size === tire.size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      return [...prevCart, { ...tire, quantity }];
    });
  };

  const removeFromCart = (tireSize) => {
    setCart(prevCart => prevCart.filter(item => item.size !== tireSize));
  };

  const updateQuantity = (tireSize, quantity) => {
    if (quantity <= 0) {
      removeFromCart(tireSize);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.size === tireSize ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('tire_cart');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.final_price || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const calculateFinalPrice = (tire, isMember = false, memberTier = 'Bronze') => {
    let price = tire.price;
    
    // Apply general discount if exists and active
    if (tire.discount_type && tire.discount_value) {
      const now = new Date();
      const saleStart = tire.sale_start_date ? new Date(tire.sale_start_date) : null;
      const saleEnd = tire.sale_end_date ? new Date(tire.sale_end_date) : null;
      
      const isSaleActive = (!saleStart || now >= saleStart) && (!saleEnd || now <= saleEnd);
      
      if (isSaleActive) {
        if (tire.discount_type === 'percentage') {
          price = price - (price * tire.discount_value / 100);
        } else if (tire.discount_type === 'fixed') {
          price = price - tire.discount_value;
        }
      }
    }
    
    // Apply member discount
    if (isMember && tire.member_only_discount) {
      price = price - (price * tire.member_only_discount / 100);
    }
    
    return Math.max(0, price);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        calculateFinalPrice
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
